import {EtsyListing} from '@coolcolduk/etsy';
import {Image} from '@coolcolduk/ui-image';
import {AlertProps, Box, Chip, Grid} from '@mui/material';
import dayjs from 'dayjs';
import {useState} from 'react';
import {UserAccount} from '../../../__generated__/resolvers-types';
import {HookAccountToken} from '../../../hook/useGetAllAccountToken';
import {firebaseAuth, getFirebaseFunction} from '../../../setup/firebase';
import validateRequestError from '../../../util/error/validateRequestError';
import {updateAllListingsByIds} from '../../../util/etsy/updateAllListingsByIds';
import EtsyUpdateListingSeoItemEditor from './EtsyUpdateListingSeoItemEditor';

export interface EtsyUpdateListingSeoItemProps {
  selectedAccount: HookAccountToken;
  listing: EtsyListing;
  user: UserAccount | null | undefined;
  /** notify parent that listing has been updated */
  updatedListing?: (listingId: number) => void;
  showMessage?: (message: string, durationMs?: number, severity?: AlertProps['severity']) => void;
}

export default function EtsyUpdateListingSeoItem({
  listing,
  selectedAccount,
  updatedListing,
  showMessage,
  user,
}: EtsyUpdateListingSeoItemProps) {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const image = listing.images?.[0];

  return (
    <>
      <Box key={listing.listing_id} sx={{p: 2, borderBottom: '1px solid #eee'}} onClick={() => setIsEditorOpen(true)}>
        <Grid container spacing={2} direction="row">
          <Grid size={{xs: 12, sm: 3, md: 2}}>
            {image && (
              <Image
                src={`${getFirebaseFunction('etsy')}/fetchFile?url=${image.url_fullxfull}`}
                width={100}
                height={100}
              />
            )}
          </Grid>
          <Grid size={{xs: 12, sm: 9, md: 2}}>
            <Grid container direction="column">
              <Grid size={{xs: 12}}>id: {listing.listing_id}</Grid>
              <Grid size={{xs: 12}}>
                last modified: {dayjs(listing.last_modified_timestamp * 1000).format('YYYY-MM-DD HH:mm:ss')}
              </Grid>
              <Grid size={{xs: 12}}>views: {listing.views}</Grid>
            </Grid>
          </Grid>
          <Grid size={{xs: 12, md: 8}}>
            <Grid container direction="column">
              <Grid size={{xs: 12}}>{listing.title}</Grid>
              <Grid container direction={{xs: 'column', sm: 'row'}}>
                {(listing.tags || []).map((tag) => (
                  <Grid size={{xs: 12, sm: 'auto'}} key={tag}>
                    <Chip label={tag} size="small" sx={{m: 0.5}} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {isEditorOpen && (
        <EtsyUpdateListingSeoItemEditor
          onSave={async (title: string, tags: string[]) => {
            const token = await firebaseAuth.currentUser?.getIdToken();
            if (!token) {
              if (showMessage) showMessage('Token error, please try to refresh the page or login again');
              return;
            }
            const res = await updateAllListingsByIds(token, selectedAccount.id, [
              {id: listing.listing_id, title, tags},
            ]);

            try {
              validateRequestError(res);
              const l = res[0];

              if (updatedListing) updatedListing(l.listing_id);
            } catch (_e) {
              if (showMessage) showMessage('Error updating listing', undefined, 'error');
            }
          }}
          listing={listing}
          user={user}
          onClose={() => setIsEditorOpen(false)}
        />
      )}
    </>
  );
}
